import type { ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Icon } from '../components/installer/icons.js'
import { PROOF_STEPS } from '../lib/proofSteps.js'
import { nextUncapturedIdx, useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

/**
 * Good-path verification result for the just-captured proof step. The current
 * photo is already in `state.captured` (dispatched by CaptureScreen before
 * navigating here), so `nextUncapturedIdx` points at the next pending step —
 * or -1 when all five are done, which routes to the summary.
 *
 * Ported from the Lovable prototype `Index.tsx` `screen-verified` (lines 644-686).
 */
export const VerifyPassedScreen = (): ReactElement | null => {
  const { state, dispatch } = useDemoStore()
  const { goto } = useStep()

  const activeStep = PROOF_STEPS[state.activeIdx]
  if (activeStep === undefined) return null

  const next = nextUncapturedIdx(state)
  const nextStep = next === -1 ? undefined : PROOF_STEPS[next]
  // The green "here's what's right" box. Use the SAME coordinates as the camera
  // screen (`cameraBox`) — NOT a separate passBox — so the box lands on the same
  // feature here as it did live. The verify image uses the camera's portrait
  // aspect (see .verify-image), so identical coords → identical placement.
  const passBox = activeStep.cameraBox[0]

  const progress = (30 + ((state.activeIdx + 1) / PROOF_STEPS.length) * 60) / 100

  const footer =
    nextStep === undefined ? (
      <Button size="xl" onClick={() => goto('summary')}>
        Continue to Summary <Icon.Chevron />
      </Button>
    ) : (
      <Button
        size="xl"
        onClick={() => {
          dispatch({ type: 'START_PHOTO', idx: next })
          goto('capture')
        }}
      >
        Next: {nextStep.num} · {nextStep.label} <Icon.Chevron />
      </Button>
    )

  return (
    <AppShell
      title="Verification passed"
      showBack
      onBack={() => goto('capture')}
      progress={progress}
      footer={footer}
    >
      <div className="installer-surface page fade-in">
        <div className="verify-image-card">
          <div
            className="verify-image"
            style={{ backgroundImage: `url(${activeStep.basePhoto})` }}
          >
            {passBox !== undefined && (
              <div
                className="detection-box-live"
                style={{
                  top: passBox.top,
                  left: passBox.left,
                  width: passBox.width,
                  height: passBox.height,
                }}
              >
                <div className="detection-label-live">{passBox.text}</div>
              </div>
            )}
          </div>
        </div>

        <div className="signal-chip">
          <div className="signal-dot" />
          Verified
        </div>

        <div className="outcome-card">
          <div className="outcome-title">{activeStep.verify.outcomeTitle}</div>
          <div className="outcome-body">{activeStep.verify.outcomeBody}</div>
        </div>
      </div>
    </AppShell>
  )
}
